import { ApplicationState } from "./../../index";
import { DistanceState, DistanceValue } from "../../Distance";
import { AddOnState, IAddOn, PopupState, PrepareAddonTexts, PrepareSelectedAddons, SetAddonVisibility, UspState } from "../../AddOn";
import { ProfileState } from "../../Profile";
import { Texts } from "../../ComponentTexts";
import { InsuranceState, Coverage, Deductible, CoveragePrice, CoverageItem } from "../../Insurance";
import { FooterState, GetUpdatedFooterPricesV2 } from "../../Footer";
import { IPaymentOption, PaymentTypeState } from "../../PaymentType";
import { GetDateFromDateTime } from "../../../helperFunctions";
import { DamageFreeYearState } from "../../DamageFreeYear";
import { BrandsEnum, IsCommercialBrand } from "../../../enums";
import WebshopDataDeliveryService from "../../../services/WebshopDataDeliveryService";
import { toErrorPayload } from "../../../helpers/errorPayload";
import { SERVER_ERROR_MESSAGE, API_HOST_ERROR_MESSAGE } from "../../../constants/errorMessages";
import { setServerError } from "../../error";
import CarImageService from "../../../services/CarImageService";

const method = "POST";
const headers = { "Content-Type": "application/json" };

type InitRequest = {
  quoteId?: string;
  brand?: string;
};

const Init = async (state: ApplicationState, dispatch?: Function): Promise<ApplicationState> => {
  if (!state.inputs.apiHost) {
    if (dispatch) dispatch(setServerError(API_HOST_ERROR_MESSAGE, SERVER_ERROR_MESSAGE, state.inputs.errorUrl));
    throw toErrorPayload({ message: API_HOST_ERROR_MESSAGE, header: SERVER_ERROR_MESSAGE, redirectUrl: state.inputs.errorUrl });
  }

  const url = `${state.inputs.apiHost}/api/se/init`;

  const data: InitRequest = {
    quoteId: state.inputs.quoteId,
    brand: state.inputs.brand,
  };

  const carImageResponse = await CarImageService(state.inputs.apiHost, state.inputs.crn, state.inputs.partner);
  const addonPopupResponse = await FetchAddonPopupData(state.inputs.cmsHost, state.inputs.webshopPageGuid);
  const addonUSPResponse = await FetchAddonUSPData(state.inputs.cmsHost, state.inputs.webshopPageGuid);

  const response = await fetch(`${url}`, {
    method,
    mode: "cors",
    headers,
    body: JSON.stringify(data),
  });

  const json = await response.json();

  if (response.status !== 200) {
    const message = (json && json.message) ?? SERVER_ERROR_MESSAGE;
    if (dispatch) dispatch(setServerError(API_HOST_ERROR_MESSAGE, SERVER_ERROR_MESSAGE, state.inputs.errorUrl));
    throw toErrorPayload({ message, header: SERVER_ERROR_MESSAGE, redirectUrl: state.inputs.errorUrl });
  }

  const coverageDescriptionsCMS = prepareCoverageDescriptions(json);
  const coverages = json.coverages?.map(
    (val: any) =>
      ({
        InternalCode: val.id,
        LongName: val.name,
        ShortName: val.name.replace("försäkring", ""),
        Description: mapCoverageDescription(
          val,
          coverageDescriptionsCMS,
          json?.vehicle?.make,
          json?.vehicle?.vehicleTypeCode,
          json?.partnerId,
        ),
        PrimaryPrice: mapCoveragePrice(val.price),
        SecondaryPrice: {},
        IsRecommended: val.isDefaultSuggestion,
        CoverageItems: mapCoverageItemsInCoverage(json.coverageItems, val.includedCoverageItems),
        Deductibles: mapDeductibles(val.deductibles),
        CoverageAddons: mapAddons(val.addons, json.addonsCDA),
        CalculatedPrice: preparePrice(val.price),
        CalculatedPriceSecondary: "",
      } as Coverage),
  );

  function MapAddonPopups(addons: IAddOn[], popups: any[]) {
    if (popups) {
      const popupState = popups as PopupState[];
      addons.forEach(x => {
        const popup = popupState.find((y: any) => y.internalAddonCode === x.InternalCode);
        if (popup) {
          x.PopupState = popup;
        }
      });
    }
    return addons;
  }

  function MapAddonUSP(addons: IAddOn[], addonUSPs: any[]) {
    if (addonUSPs) {
      const uspState = addonUSPs as UspState[];
      addons.forEach(x => {
        const addonUSP = uspState.find((y: any) => y.internalAddonCode === x.InternalCode);
        if (addonUSP) {
          x.UspState = addonUSP;
        }
      });
    }
    return addons;
  }

  const currentCoverage = coverages.find((x: any) => x.IsRecommended === true);

  const selectedAddonInternalCodes =
    currentCoverage?.addons
      ?.filter((x: any) => x.Checked)
      ?.map((x: IAddOn) => x.InternalCode)
      ?.filter((x?: string) => x != null) ?? [];

  const currentCoverageItems = currentCoverage.CoverageItems;

  const mileages: DistanceValue[] =
    json.mileage?.availableMileageOptions?.map((i: any) => ({
      title: i.description,
      value: i.id,
    })) ?? [];
  let addOns = PrepareSelectedAddons(currentCoverage?.CoverageAddons, selectedAddonInternalCodes);
  addOns = MapAddonPopups(addOns, addonPopupResponse.addonPopupBlocks);
  addOns = MapAddonUSP(addOns, addonUSPResponse.addonUSPBlocks);

  const footerPrices = GetUpdatedFooterPricesV2(state.footer as FooterState, currentCoverage, selectedAddonInternalCodes);

  const selectedDeductible = currentCoverage?.Deductibles?.find((i: any) => i.IsSelected === true)?.Code;

  return {
    ...state,
    youngDriver: {
      ...state.youngDriver,
      questionVisible: false,
      selected: json.priceCalculationParameters?.driverBelow23YearsOfAge ?? false,
      title: json.uiContent?.YoungDriver?.Header,
      values: [
        {
          value: true,
          text: "Ja",
        },
        {
          value: false,
          text: "Nei",
        },
      ],
      description: {
        text: json.uiContent?.YoungDriver?.Popup.Body,
        title: json.uiContent?.YoungDriver?.Popup.Header,
      },
    },
    damageFreeYear: {
      ...state.damageFreeYear,
      title: json.uiContent?.TrafficDamage.Header,
      questionVisible: IsCommercialBrand(state.inputs.brand as BrandsEnum) ? false : true,
      selected: json.vehicle.hasHadAccident,
      description: {
        text: json.uiContent?.TrafficDamage.Popup.Body,
        title: json.uiContent?.TrafficDamage.Popup.Header,
      },
    } as DamageFreeYearState,
    profile: {
      ...state.profile,
      name: json.policyHolder?.companyName ?? json.policyHolder?.name,
      isCarNew: json.vehicle.carCondition === "Used" ? false : true,
      encryptedSSN: json.policyHolder?.encryptedOrganizationNumber ?? undefined,
      email: {
        title: json.uiContent?.Email?.Header,
        placeholderText: json.uiContent?.Email?.PlaceHolder,
        state: 0,
        stateDescriptions: [undefined, json.uiContent?.EmailRequiredErrorMessage, json.uiContent?.EmailIncorrectErrorMessage],
        value: "",
      },
      phone: {
        title: json.uiContent?.PhoneNumber?.Header,
        placeholderText: json.uiContent?.PhoneNumber?.PlaceHolder,
        state: 0,
        stateDescriptions: [undefined, json.uiContent?.PhoneRequiredErrorMessage, json.uiContent?.PhoneIncorrectErrorMessage],
        value: "",
      },
      carImageUrl: carImageResponse?.carImageUrl,
      isORG: !!json.policyHolder?.encryptedOrgN,
      carModel: `${json.vehicle.model} ${json.vehicle.modelYear}`,
      carYear: json.vehicle.modelYear,
      encryptedRegistrationNumber: json.vehicle?.encryptedRegistrationNumber,
      isValid: true,
      actualMilage: {
        title: json.uiContent?.ActualMilageLabel,
        placeholderText: json.uiContent?.ActualMilagePlaceHolder,
        state: 0,
        stateDescriptions: [undefined, json.uiContent?.ActualMilageRequired, json.uiContent?.ActualMilageIncorrect],
        value: "",
      },
      gdpr: {
        questionVisible: true,
        selected: false,
        text: json.uiContent?.GDPRText,
        requiredErrorMessage: json.uiContent?.GDPRRequired,
        showError: false,
        readMore: "Les mer.",
        readMoreUrl: json.uiContent?.GDPRUrl,
      },
    } as ProfileState,

    addon: {
      addOns: addOns,
      addonsCMS: json.addonsCMS,
      selectedAddOnInternalCodes: selectedAddonInternalCodes,
      title: json.uiContent?.AddonsHeader ?? state.addon?.title,
    } as AddOnState,
    distance: {
      ...state.distance,
      showMileageTextForTaxi: json?.vehicle?.vehicleTypeCode === "05" ? true : false,
      values: mileages,
      selected: json.mileage?.selectedMileageOptionId,
      title: json.uiContent?.MileageHeader || "",
      label: {
        title: json.uiContent?.Mileage?.Header,
      },
      help: {
        title: json.uiContent?.Mileage?.Popup?.Header,
        description: json.uiContent?.Mileage?.Popup?.Body,
      },
      mileageTextTaxi: json.uiContent?.MileageTextTaxi,
    } as DistanceState,
    texts: {
      redirectButtonText: json.uiContent?.ConfirmationRedirectBtnText,
      confirmationHeader: json.uiContent?.ConfirmationHeader,
      confirmationBody: json.uiContent?.ConfirmationBody,
      confirmationExtraInfo: json.uiContent?.ConfirmationExtraInfo,
      confirmationExtraUrl1: {
        urlText: json.uiContent?.ConfirmationExtraUrl1?.Text,
        link: json.uiContent?.ConfirmationExtraUrl1?.Url,
      },
      confirmationExtraUrl2: {
        urlText: json.uiContent?.ConfirmationExtraUrl2?.Text,
        link: json.uiContent?.ConfirmationExtraUrl2?.Url,
      },
      welcomeHeaderPartOneText: (json.uiContent?.WelcomeHeaderPartOneText ?? state.texts?.welcomeHeaderPartOneText)?.trim(),
      ssnHeader: json.uiContent?.SSNHeader ?? state.texts?.ssnHeader,
      orgHeader: json.uiContent?.ORNHeader ?? state.texts?.orgHeader,
      registrationNumberHeader: json.uiContent?.RegistrationNumberHeader ?? state.texts?.registrationNumberHeader,
      carModelHeader: json.uiContent?.CarModelHeader ?? state.texts?.carModelHeader,
      checkoutButtonText: json.uiContent?.CheckoutButtonText,
      coverageHeader: json.uiContent?.CoverageHeader ?? state.texts?.coverageHeader,
      changeChoiceText: json.uiContent?.ChangeChoiceButtonText ?? state.texts?.changeChoiceText,
      billingSectionHeader: json.uiContent?.BillingHeader ?? state.texts?.billingSectionHeader,
      trialPeriodMessage: json.uiContent?.Withdrawal.Popup.Header,
      infoBlockText: json.uiContent?.InfoBlockText,
      preliminaryPriceText: json.uiContent?.PreliminaryPriceText,
      antiTheftMessage: json.antiTheftMessage,
      confirmationLinks: chooseConfirmationLinks(state, json),
      confirmationPromotion1: {
        body: json.uiContent?.ConfirmationPromotion1?.Body,
        header: json.uiContent?.ConfirmationPromotion1?.Header,
        link: {
          text: json.uiContent?.ConfirmationPromotion1?.Link.Text,
          url: json.uiContent?.ConfirmationPromotion1?.Link.Url,
        },
      },
      singleRecommendedButton: json.uiContent?.SingleRecomendedCoverageButton,
      discountMessageBoxText: json.uiContent?.DiscountMessageBoxText ?? "Vi har trukket fra rabatter pa",
      confirmationBottomSection: {
        body: json.uiContent?.ConfirmationBottomSection?.Body,
        header: json.uiContent?.ConfirmationBottomSection?.Header,
        link: {
          text: json.uiContent?.ConfirmationBottomSection?.Link.Text,
          url: json.uiContent?.ConfirmationBottomSection?.Link.Url,
        },
      },
      persona: json.uiContent?.Persona && {
        name: json.uiContent?.Persona?.Name,
        subtitle: json.uiContent?.Persona?.Subtitle,
        imageUrl: json.uiContent?.Persona?.ImageUrl,
      },
      confirmationExistingCustomerHeader: json.uiContent?.ConfirmationExistingCustomerHeader,
      confirmationWithInstructionsHeader: json.uiContent?.ConfirmationWithInstructionsHeader,
      confirmationWithInstructionsBody: json.uiContent?.ConfirmationWithInstructionsBody,
      confirmationManualInstructionsHeader: json.uiContent?.ConfirmationManualInstructionsHeader,
      confirmationInsuranceStartDate: json.uiContent?.ConfirmationInsuranceStartDate ?? "Gjelder fra",
      customisePageButton: json.uiContent?.CoveragePageButtonText,
      actualMilageHeader: json.uiContent?.ActualMilageHeader,
      BonusSectionHeader: json.uiContent?.BonusSectionHeader,
      contactsSectionHeader: json.uiContent?.ContactsSectionHeader,
      addonDescriptionMotor: json.uiContent?.AddonDescriptionMotor,
      addonDescriptionLeiebil: json.uiContent?.AddonDescriptionLeiebil,
      addonMotorCheckboxDescription: json.uiContent?.AddonMotorCheckboxDescription,
      addonCheckboxErrorMessage: json.uiContent?.AddonCheckboxErrorMessage,
      commercialTaxiMessage: json.uiContent?.CommercialTaxiMessage,
      coverageDescriptions: {
        coverageDescriptionSuper: json.uiContent?.CoverageDescriptionSuper,
        coverageDescriptionKasko: json.uiContent?.CoverageDescriptionKasko,
        coverageDescriptionDelkasko: json.uiContent?.CoverageDescriptionDelkasko,
        coverageDescriptionAnsvar: json.uiContent?.CoverageDescriptionAnsvar,
        coverageDescription_T1: coverageDescriptionsCMS?.coverageDescription_T1,
        coverageDescription_T1D1: coverageDescriptionsCMS?.coverageDescription_T1D1,
        coverageDescription_T1D1V1: coverageDescriptionsCMS?.coverageDescription_T1D1V1,
        volviaComercialRecommendationOtherBrand_T1D1: coverageDescriptionsCMS?.volviaComercialRecommendationOtherBrand_T1D1,
        volviaComercialRecommendationOtherBrand_T1D1V1: coverageDescriptionsCMS?.volviaComercialRecommendationOtherBrand_T1D1V1,
        commercialCoverageDescriptionTraficLightTruck: coverageDescriptionsCMS?.commercialCoverageDescriptionTraficLightTruck,
        commercialCoverageDescriptionHalvLightTruck: coverageDescriptionsCMS?.commercialCoverageDescriptionHalvLightTruck,
        commercialCoverageDescriptionHelLightTruck: coverageDescriptionsCMS?.commercialCoverageDescriptionHelLightTruck,
        commercialRecommendedCoverageDescriptionHelLightTruck:
          coverageDescriptionsCMS?.commercialRecommendedCoverageDescriptionHelLightTruck,
        coverageRecommendationDescription_T1: coverageDescriptionsCMS?.coverageRecommendationDescription_T1,
        coverageRecommendationDescription_T1D1: coverageDescriptionsCMS?.coverageRecommendationDescription_T1D1,
        coverageRecommendationDescription_T1D1V1: coverageDescriptionsCMS?.coverageRecommendationDescription_T1D1V1,
      },
    } as Texts,
    footer: {
      ...footerPrices,
      totalPriceTooltip: json.uiContent?.TotalPriceTooltipText,
      purchaseButton: json.uiContent?.BuyButtonText,
      expanded: false,
      secondaryPrice: currentCoverage?.SecondaryPrice,
    } as FooterState,
    singleRecomendation: {
      title: json.uiContent?.RecommendationHeaderLabel,
      text: json.uiContent?.RecommendationText,
      coverageItems: currentCoverageItems,
      readMore: {
        text: json.uiContent?.RecommendationReadMoreText,
        linkText: "Läs mer.",
        title: json.uiContent?.RecommendationReadMoreTitle,
      },
    },
    insurance: {
      ...state.insurance,
      avatarEnabled: json.avatarEnabled,
      coverage: {
        selectedCode: currentCoverage.InternalCode,
        selectedName: currentCoverage.Name,
        Coverages: coverages,
        SelectedDeductible: selectedDeductible,
        CoverageItems: mapCoverageItems(json.coverageItems, coverages),
        recommendedTitle: json.uiContent?.RecommendedCoverageLabel ?? state.insurance?.coverage?.recommendedTitle,
        IsSingleRecommened: json.singleRecomendedCoverageEnabled,
        isCarUnder3YearsAndNotImported: false,
        ui: {
          deductible: {
            label: json.uiContent?.Deductible.Header,
            helpHeader: json.uiContent?.Deductible.Popup.Header,
            helpText: json.uiContent?.Deductible.Popup.Body,
          },
          coverageSelect: {
            label: json.uiContent?.SelectCoverageHeader ?? "Välj försäkring",
          },
        },
      },
      startDate: {
        title: json.uiContent?.StartDate.Header,
        value: json?.StartDate ? GetDateFromDateTime(new Date(json?.StartDate)) : undefined,
        helperTextFromUi: json?.uiContent?.StartDateIncorrectErrorMessage,
      },
    } as InsuranceState,
    vehicle: {
      regNoQuestion: {
        title: json.uiContent?.VehicleRegNoQuestionTitle,
        selected: false,
        values: [
          {
            value: true,
            text: "Ja",
          },
          {
            value: false,
            text: "Nej",
          },
        ],
        requiredErrorMessage: json.uiContent?.VehicleRegNoQuestionError,
        showError: false,
        help: {
          title: json.uiContent?.VehicleRegNoQuestionHelpTitle,
          description: json.uiContent?.VehicleRegNoQuestionHelpDescription,
        },
      },
      regNo: {
        title: json.uiContent?.VehicleRegNoTitle,
        stateDescriptions: [undefined, json.uiContent?.VehicleRegNoRequired, json.uiContent?.VehicleRegNoIncorrect],
      },
      VINNo: {
        title: json.uiContent?.VehicleVINNoTitle,
        stateDescriptions: [undefined, json.uiContent?.VehicleVINNoRequired, json.uiContent?.VehicleVINNoIncorrect],
        description: {
          title: json.uiContent?.VehicleVINNoHelpTitle,
          text: json.uiContent?.VehicleVINNoHelpDescription,
        },
      },
    },
    payment: {
      ...state.payment,
      selectedPaymentTypeCode: json.paymentOptions.selectedPaymentMethodAndFrequencyId,
      paymentTypes: mapPayments(json),
      sectionHeader: json.uiContent?.PaymentMethod?.Header ?? state.payment?.sectionHeader,
      inputs: {
        bankAccountClearingNumber: {
          title: json.uiContent?.ClearingNumber?.Header,
          placeholderText: json.uiContent?.ClearingNumber?.PlaceHolder,
          state: 0,
          value: "",
          stateDescriptions: [
            undefined,
            json.uiContent?.ClearingNumberRequiredErrorMessage,
            json.uiContent?.ClearingNumberIncorrectErrorMessage,
          ],
          description: {
            text: json.uiContent?.ClearingNumber?.Popup?.Body,
            title: json.uiContent?.ClearingNumber?.Popup?.Header,
          },
        },
        bankAccountNumber: {
          title: json.uiContent?.BankAccount?.Header,
          placeholderText: json.uiContent?.BankAccount?.PlaceHolder,
          state: 0,
          value: "",
          stateDescriptions: [undefined, json.uiContent?.BankAccountRequiredErrorMessage, json.uiContent?.BankAccountIncorrectErrorMessage],
          description: {
            text: json.uiContent?.BankAccount?.Popup?.Body,
            title: json.uiContent?.BankAccount?.Popup?.Header,
          },
        },
        bankAccountOwnerName: {
          title: json.uiContent?.AccountOwnerName?.Header,
          placeholderText: json.uiContent?.AccountOwnerName?.PlaceHolder,
          state: 0,
          value: "",
          stateDescriptions: [
            undefined,
            json.uiContent?.AccountOwnerNameRequiredErrorMessage,
            json.uiContent?.AccountOwnerNameIncorrectErrorMessage,
          ],
          description: {
            text: json.uiContent?.AccountOwnerName?.Popup?.Body,
            title: json.uiContent?.AccountOwnerName?.Popup?.Header,
          },
        },
        bankAccountOwnerSSN: {
          title: json.uiContent?.AccountOwnerSSN?.Header,
          placeholderText: json.uiContent?.AccountOwnerSSN?.PlaceHolder,
          state: 0,
          value: "",
          stateDescriptions: [
            undefined,
            json.uiContent?.AccountOwnerSSNRequiredErrorMessage,
            json.uiContent?.AccountOwnerSSNIncorrectErrorMessage,
          ],
          description: {
            text: json.uiContent?.AccountOwnerSSN?.Popup?.Body,
            title: json.uiContent?.AccountOwnerSSN?.Popup?.Header,
          },
        },
        bankAccountOwner: {
          label: json.uiContent?.AccountOwner,
        },
      },
      digitalSigning: {
        showMessage: true,
        message: json.uiContent?.SignedConsentMessage,
      },
    } as PaymentTypeState,
    inputs: {
      ...state.inputs,
      vehicle: json.vehicle,
    },
  } as ApplicationState;
  // return state;
};

export default Init;

export function preparePrice(price: any) {
  return `${price.amount} ${price.currency}/${price.frequency}`;
}

export function mapAddons(addOnsToMap: any, addonsContent: any) {
  const list = addOnsToMap
    ?.filter((i: any) => !!i)
    ?.map(
      (val: any) =>
        ({
          InternalCode: val.id,
          Name: val.name,
          Description: val.description,
          Exclude: addonsContent?.find((x: any) => x.internalCode === val.id)?.bundledAddons ?? [],
          MonthlyPrice: preparePrice(val.price),
          PopupText: addonsContent?.find((x: any) => x.internalCode === val.id)?.popupTextSv ?? "",
          SortOrder: addonsContent?.find((x: any) => x.internalCode === val.id)?.sortOrder ?? "",
          UspState: addonsContent?.find((x: any) => x.internalCode === val.id)?.UspState ?? "",
          PopupState: addonsContent?.find((x: any) => x.internalCode === val.id)?.PopupState ?? "",
          VariablePrice: "",
          visible: true,
          disabled: false,
          campaigned: false,
          Checked: val.isSelected,
        } as IAddOn),
    );

  return PrepareAddonTexts(list.sort((x: any, y: any) => x.SortOrder - y.SortOrder));
}

export function mapCoverageItems(coverageItemsToMap: any, coverages: any) {
  const coverageItems: CoverageItem[] =
    coverageItemsToMap?.map(
      (val: any) =>
        ({
          InternalCode: val.id,
          Name: val.name,
          Description: val.description,
          ItemDeductible: val.deductible ?? undefined,
        } as CoverageItem),
    ) ?? [];

  return coverageItems;
}

export function mapAddonsWithContentInState(addOnsToMap: any, addonsContent: any) {
  const list = addOnsToMap
    ?.filter((i: any) => !!i)
    ?.map(
      (val: any) =>
        ({
          InternalCode: val.id,
          Name: val.name,
          Description: val.description,
          Exclude: addonsContent?.find((x: IAddOn) => x.InternalCode === val.id)?.Exclude ?? [],
          MonthlyPrice: preparePrice(val.price),
          PopupText: addonsContent?.find((x: IAddOn) => x.InternalCode === val.id)?.PopupTextSv ?? "",
          SortOrder: addonsContent?.find((x: IAddOn) => x.InternalCode === val.id)?.SortOrder ?? "",
          UspState: addonsContent?.find((x: IAddOn) => x.InternalCode === val.id)?.UspState ?? "",
          PopupState: addonsContent?.find((x: IAddOn) => x.InternalCode === val.id)?.PopupState ?? "",
          VariablePrice: "",
          visible: true,
          disabled: false,
          campaigned: false,
          Checked: val.isSelected,
        } as IAddOn),
    );
  return PrepareAddonTexts(list.sort((x: IAddOn, y: IAddOn) => x.SortOrder - y.SortOrder));
}

export function mapCoverageItemsInCoverage(coverageItemsSource: any, includedCoverageItems: string[]) {
  const coverageItems: CoverageItem[] =
    coverageItemsSource?.map(
      (val: any) =>
        ({
          InternalCode: val.id,
          Name: val.name,
          Description: val.description,
          ItemDeductible: val.deductible ?? undefined,
        } as CoverageItem),
    ) ?? [];
  return coverageItems.filter((i: CoverageItem) => includedCoverageItems.includes(i.InternalCode));
}

export function mapDeductibles(deductiblesToMap: any) {
  if (deductiblesToMap && deductiblesToMap.length) {
    return (
      deductiblesToMap?.map(
        (i: any) =>
          ({
            Code: i.id,
            Name: i.value,
            IsSelected: i.isSelected,
          } as Deductible),
      ) ?? []
    );
  }
  return undefined;
}

export function mapCoveragePrice(monthlyPrice: any) {
  return {
    Amount: monthlyPrice?.amount,
    Currency: monthlyPrice?.currency,
    Period: monthlyPrice?.frequency,
  } as CoveragePrice;
}

export function chooseConfirmationLinks(state: ApplicationState, json: any) {
  return {
    checkoutExtraUrl1: {
      urlText: json.termsAndRelatedInformation[0].name,
      link: json.termsAndRelatedInformation[0].link,
    },
  };
}

function prepareCoverageDescriptions(json: any) {
  return {
    coverageDescription_T1: json?.uiContent?.CoverageDescription_T1,
    coverageDescription_T1D1: json?.uiContent?.CoverageDescription_T1D1,
    coverageDescription_T1D1V1: json?.uiContent?.CoverageDescription_T1D1V1,
    volviaComercialRecommendationOtherBrand_T1D1: json?.uiContent?.VolviaComercialRecomentadionOtherBrand_T1D1,
    volviaComercialRecommendationOtherBrand_T1D1V1: json?.uiContent?.VolviaComercialRecomentadionOtherBrand_T1D1V1,
    commercialCoverageDescriptionTraficLightTruck: json?.uiContent?.CommercialCoverageDescriptionTraficLightTruck,
    commercialCoverageDescriptionHalvLightTruck: json?.uiContent?.CommercialCoverageDescriptionHalvLightTruck,
    commercialCoverageDescriptionHelLightTruck: json?.uiContent?.CommercialCoverageDescriptionHelLightTruck,
    commercialRecommendedCoverageDescriptionHelLightTruck: json?.uiContent?.CommercialRecommendedCoverageDescriptionHelLightTruck,
    coverageRecommendationDescription_T1: json?.uiContent?.CoverageRecommendationDescription_T1,
    coverageRecommendationDescription_T1D1: json?.uiContent?.CoverageRecommendationDescription_T1D1,
    coverageRecommendationDescription_T1D1V1: json?.uiContent?.CoverageRecommendationDescription_T1D1V1,
  };
}

// Here we are mapping the Coverage recommentadions and descriptions!
// api v2 are not sending the data, that`s why we are fetching them from gadget
export function mapCoverageDescription(
  coverage: any,
  coverageDescriptions: any,
  brand: string,
  vehicleTypeCode: string,
  partnerId: string,
) {
  const vrfBrands = ["volvo", "dacia", "renault", "polestar"];
  let coverageDescription;

  switch (coverage?.name) {
    case "Trafikförsäkring":
      coverageDescription = coverageDescriptions?.coverageDescription_T1;
      break;
    case "Halvförsäkring":
      coverageDescription = coverageDescriptions?.coverageDescription_T1D1;
      break;
    case "Helförsäkring":
      coverageDescription = coverageDescriptions?.coverageDescription_T1D1V1;
      break;
  }
  if (partnerId === "VCWS") {
    if (!vrfBrands.includes(brand.toLowerCase())) {
      if (coverage?.isDefaultSuggestion) {
        switch (coverage?.name) {
          case "Halvförsäkring":
            coverageDescription = coverageDescriptions?.volviaComercialRecomentadionOtherBrand_T1D1;
            break;
          case "Helförsäkring":
            coverageDescription = coverageDescriptions?.volviaComercialRecomentadionOtherBrand_T1D1V1;
            break;
        }
      }
    }
    if (vehicleTypeCode === "50") {
      switch (coverage.name) {
        case "Trafikförsäkring":
          coverageDescription = coverageDescriptions?.commercialCoverageDescriptionTraficLightTruck;
          break;
        case "Halvförsäkring":
          coverageDescription = coverageDescriptions?.commercialCoverageDescriptionHalvLightTruck;
          break;
        case "Helförsäkring":
          coverageDescription = coverageDescriptions?.commercialCoverageDescriptionHelLightTruck;
          break;
      }
      if (coverage?.isDefaultSuggestion) {
        switch (coverage?.name) {
          case "Halvförsäkring":
            // This is made on purpose, because the description is the same for both Halvförsäkring and Helförsäkring
            coverageDescription = coverageDescriptions?.commercialRecommendedCoverageDescriptionHelLightTruck;
            break;
          case "Helförsäkring":
            coverageDescription = coverageDescriptions?.commercialRecommendedCoverageDescriptionHelLightTruck;
            break;
        }
      }
    }
  } else {
    if (coverage?.isDefaultSuggestion) {
      switch (coverage?.name) {
        case "Trafikförsäkring":
          coverageDescription = coverageDescriptions?.coverageRecommendationDescription_T1;
          break;
        case "Halvförsäkring":
          coverageDescription = coverageDescriptions?.coverageRecommendationDescription_T1D1;
          break;
        case "Helförsäkring":
          coverageDescription = coverageDescriptions?.coverageRecommendationDescription_T1D1V1;
          break;
      }
    }
  }

  if (coverageDescription) return coverageDescription;
  else return null;
}

export function mapPayments(json: any) {
  const availableCodes = json?.paymentOptions.availablePaymentFrequencyAndMethods.map((x: any) => x.id);
  const paymentOptions = json?.paymentsCMS
    .filter((val: any) => availableCodes.includes(val.Code))
    .map(
      (val: any) =>
        ({
          code: val?.Code,
          selected: val?.Code === json.paymentOptions.selectedPaymentMethodAndFrequencyId ? true : false,
          title: val?.Name,
          available: true,
          description: val?.Description,
          secondaryText: val?.SecondaryText,
          paymentPeriods: val?.PaymentPeriods,
        } as IPaymentOption),
    );

  return paymentOptions;
}

export async function FetchAddonPopupData(cmsHost: string, webshopPageGuid: string) {
  return await WebshopDataDeliveryService(cmsHost, "getAddonPopupBlocks", webshopPageGuid);
}

export async function FetchAddonUSPData(cmsHost: string, webshopPageGuid: string) {
  return await WebshopDataDeliveryService(cmsHost, "getAddonUSPBlocks", webshopPageGuid);
}

export function prepareAddons(coverages: any, selectedAddonInternalCodes: any) {
  const currentCoverage = coverages.find((x: any) => x.IsRecommended === true);
  let addOns: any[] = [];
  coverages?.forEach((x: any) => {
    addOns = [...addOns, ...x.CoverageAddons];
    addOns.sort((a: IAddOn, b: IAddOn) => a.SortOrder - b.SortOrder);
  });

  // adding Excluded addons
  coverages?.forEach((coverage: any) => {
    coverage?.CoverageAddons?.forEach((addon: any) => {
      addOns.forEach(element => {
        if (element.InternalCode === addon.InternalCode) {
          element.Exclude = [...element.Exclude, ...addon.Exclude];
        }
        element.Exclude = element.Exclude.filter((item: any, index: any) => element.Exclude.indexOf(item) === index);
      });
    });
  });

  const uniqueAddons = addOns.filter((obj, index) => addOns.findIndex(item => item.InternalCode === obj.InternalCode) === index);

  addOns = SetAddonVisibility(
    uniqueAddons,
    currentCoverage.CoverageAddons?.map((x: any) => x.InternalCode)?.filter((x: any) => x != null) as string[],
    selectedAddonInternalCodes,
  );
  addOns = PrepareAddonTexts(addOns as IAddOn[]);
  addOns = PrepareSelectedAddons(addOns as IAddOn[], selectedAddonInternalCodes);

  return addOns;
}
